import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  BPSBadge,
  BPSCalculation,
  Button,
  GroupStatusBadge,
  FallbackGroupIcon,
  LoadingPage,
  DepositRiskThresholdSelect,
  type DepositRiskThresholdValue,
} from '@valiant/ui-shared'
import { Input } from '@valiant/ui-shared'
import { Label } from '@valiant/ui-shared'
import { Card, CardContent, CardHeader, CardTitle } from '@valiant/ui-shared'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@valiant/ui-shared'
import {
  useGetGroupQuery,
  useGetLiquidityProvidersQuery,
  useUpdateGroupMutation,
  useUpdateGroupLiquidityProviderMutation,
  type DepositRiskThreshold,
  GroupStatus,
} from '@/generated/graphql'
import { toast } from 'sonner'
import { MainLayout } from '@/components/layout/MainLayout'
import { FileUpload } from '@valiant/ui-shared'

export function ManageGroup() {
  const { groupId } = useParams<{ groupId: string }>()
  const navigate = useNavigate()

  const [{ data: groupData, fetching: groupLoading }] = useGetGroupQuery({
    variables: { groupId: groupId || '' },
    pause: !groupId,
  })
  const [{ data: liquidityProvidersData, fetching: liquidityProvidersLoading }] =
    useGetLiquidityProvidersQuery()

  const [updateGroupResult, updateGroup] = useUpdateGroupMutation()
  const [updateGroupLpResult, updateGroupLp] = useUpdateGroupLiquidityProviderMutation()

  const group = groupData?.getGroup
  const liquidityProviders = useMemo(
    () => liquidityProvidersData?.getLiquidityProviders || [],
    [liquidityProvidersData?.getLiquidityProviders],
  )

  // Form state
  const [name, setName] = useState('')
  const [status, setStatus] = useState<GroupStatus>(GroupStatus.Active)
  const [bps, setBps] = useState('50')
  const [selectedLpId, setSelectedLpId] = useState<string>('')
  const [imageUrl, setImageUrl] = useState('')
  const [depositRiskThreshold, setDepositRiskThreshold] =
    useState<DepositRiskThresholdValue>('MEDIUM')

  // Track if form has changes
  const hasChanges = useMemo(() => {
    if (!group) return false

    return (
      name !== group.name ||
      status !== group.status ||
      parseInt(bps) !== group.bps ||
      imageUrl !== (group.imageUrl || '') ||
      selectedLpId !== group.liquidityProviderId?.toString() ||
      depositRiskThreshold !== group.defaultDepositRiskThreshold
    )
  }, [group, name, status, bps, imageUrl, selectedLpId, depositRiskThreshold])

  // Set initial values when group data loads
  useEffect(() => {
    if (group) {
      setName(group.name)
      setStatus(group.status)
      setBps(group.bps.toString())
      setImageUrl(group.imageUrl || '')
      setSelectedLpId(group.liquidityProviderId?.toString() || '')
      setDepositRiskThreshold(group.defaultDepositRiskThreshold as DepositRiskThresholdValue)
    }
  }, [group])

  const isSubmitting = updateGroupResult.fetching || updateGroupLpResult.fetching

  const groupBalanceUsd = useMemo(() => {
    if (!group?.usdValueOfAvailableBalance) return 0
    return parseFloat(group.usdValueOfAvailableBalance.replace(/[$,]/g, '')) || 0
  }, [group?.usdValueOfAvailableBalance])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!group) return

    try {
      // Check what has changed and needs to be updated
      const hasNameChanged = name !== group.name
      const hasStatusChanged = status !== group.status
      const hasBpsChanged = parseInt(bps) !== group.bps
      const hasImageChanged = imageUrl !== (group.imageUrl || '')
      const hasLpChanged = selectedLpId !== group.liquidityProviderId?.toString()
      const hasRiskThresholdChanged = depositRiskThreshold !== group.defaultDepositRiskThreshold

      // Update group details if any basic fields changed
      if (
        hasNameChanged ||
        hasStatusChanged ||
        hasBpsChanged ||
        hasImageChanged ||
        hasRiskThresholdChanged
      ) {
        const updateInput: {
          groupId: string
          name?: string
          status?: GroupStatus
          bps?: number
          imageUrl?: string
          defaultDepositRiskThreshold?: DepositRiskThreshold
        } = { groupId: groupId! }
        if (hasNameChanged) updateInput.name = name
        if (hasStatusChanged) updateInput.status = status
        if (hasBpsChanged) updateInput.bps = parseInt(bps)
        if (hasImageChanged) updateInput.imageUrl = imageUrl
        if (hasRiskThresholdChanged) {
          updateInput.defaultDepositRiskThreshold = depositRiskThreshold as DepositRiskThreshold
        }
        const result = await updateGroup({ input: updateInput })
        if (result.error) {
          toast.error('Failed to update group', {
            description: result.error.message,
          })
          return
        }
      }

      // Update liquidity provider if changed
      if (hasLpChanged && selectedLpId) {
        const result = await updateGroupLp({
          input: {
            groupId: groupId!,
            liquidityProviderId: parseInt(selectedLpId),
          },
        })
        if (result.error) {
          toast.error('Failed to update liquidity provider', {
            description: result.error.message,
          })
          return
        }
      }

      // Success
      toast.success('Group updated successfully!')
      navigate(`/groups/${groupId}`)
    } catch (error) {
      toast.error('Failed to update group', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      })
    }
  }

  const selectedLp = liquidityProviders.find((lp) => lp.id === selectedLpId)

  const getStatusColor = (status: GroupStatus) => {
    return status === GroupStatus.Active ? 'text-green-600' : 'text-red-600'
  }

  const getStatusLabel = (status: GroupStatus) => {
    return status === GroupStatus.Active ? 'Active' : 'Deactivated'
  }

  const handleUploadRequest = async (_file: File) => {
    const result = await updateGroup({
      input: {
        groupId: groupId!,
        imageUrl: 'UPLOAD',
      },
    })

    if (result.error) {
      throw new Error(result.error.message)
    }

    // Check if the result has upload data
    const uploadData = result.data?.updateGroup?.uploadData

    if (uploadData) {
      return {
        uploadUrl: uploadData.uploadUrl,
        fileUrl: uploadData.fileUrl,
        fileName: uploadData.fileName,
      }
    }

    throw new Error('Upload data not received from server')
  }

  const handleImageUploaded = async (fileUrl: string) => {
    try {
      // Auto-save the image URL immediately
      const result = await updateGroup({
        input: {
          groupId: groupId!,
          imageUrl: fileUrl,
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      // Update local state on success
      setImageUrl(fileUrl)
      toast.success('Group logo updated successfully')
    } catch (error) {
      toast.error('Failed to save group logo')
      console.error('Error saving group logo:', error)
    }
  }

  const badges = useMemo(() => {
    if (!group) return []
    return [
      {
        node: <GroupStatusBadge status={group.status} />,
        direction: 'LEFT',
      },
      {
        node: <BPSBadge bps={group.totalFeeBps} />,
        direction: 'RIGHT',
      },
    ]
  }, [group])

  if (groupLoading) {
    return <LoadingPage />
  }

  if (!group) {
    return (
      <MainLayout>
        <div>Group not found</div>
      </MainLayout>
    )
  }

  return (
    <MainLayout
      breadcrumbs={[
        {
          label: group.name,
          onClick: () => navigate(`/groups/${groupId}`),
          badges: badges,
          imageUrl: group.imageUrl || undefined,
          fallbackImage: <FallbackGroupIcon />,
        },
      ]}
      title="Manage Group"
    >
      <div className="grid gap-8 md:grid-cols-2">
        <h2 className="md:hidden text-xl font-semibold">Manage Group</h2>
        {/* Manage Group Form */}
        <Card>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Group Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter group name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={status} onValueChange={(value) => setStatus(value as GroupStatus)}>
                  <SelectTrigger id="status">
                    <SelectValue>
                      <span className={getStatusColor(status)}>{getStatusLabel(status)}</span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={GroupStatus.Active}>
                      <span className="text-green-600">Active</span>
                    </SelectItem>
                    <SelectItem value={GroupStatus.Deactivated}>
                      <span className="text-red-600">Deactivated</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bps">BPS (Basis Points)</Label>
                <Input
                  id="bps"
                  type="number"
                  min="30"
                  step="1"
                  max="10000"
                  value={bps}
                  onChange={(e) => {
                    const value = e.target.value

                    // Prevent minus sign input
                    if (value.includes('-')) {
                      return
                    }

                    // Allow empty value for typing
                    if (value === '') {
                      setBps(value)
                      return
                    }

                    const numValue = parseInt(value)

                    // Only allow integers >= 0
                    if (Number.isInteger(numValue) && numValue >= 0) {
                      setBps(value)
                    }
                  }}
                  onBlur={() => {
                    // Auto-correct to 30 if below 30 on blur
                    if (bps === '' || parseInt(bps) < 30) {
                      setBps('30')
                    }
                  }}
                  placeholder="30"
                  required
                />
                <p className="text-sm text-muted-foreground">
                  Group-specific BPS (30-10000 basis points)
                </p>
              </div>

              <DepositRiskThresholdSelect
                value={depositRiskThreshold}
                onValueChange={setDepositRiskThreshold}
                disabled={isSubmitting}
                helperText="Updating this applies the new threshold to every company in the group."
              />

              <div className="space-y-2">
                <Label htmlFor="liquidity-provider">Liquidity Provider</Label>
                {liquidityProvidersLoading ? (
                  <div className="text-sm text-muted-foreground">
                    Loading liquidity providers...
                  </div>
                ) : (
                  <Select
                    key={`select-${liquidityProviders.length}`}
                    value={selectedLpId}
                    onValueChange={(value) => {
                      // Prevent setting empty string unless it's a valid option
                      if (value !== '' || liquidityProviders.some((lp) => lp.id === '')) {
                        setSelectedLpId(value)
                      }
                    }}
                    disabled={groupBalanceUsd >= 1}
                  >
                    <SelectTrigger id="liquidity-provider">
                      <SelectValue placeholder="Select a liquidity provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {liquidityProviders.map((lp) => (
                        <SelectItem key={lp.id} value={lp.id}>
                          {lp.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {groupBalanceUsd >= 1 && (
                  <p className="text-sm text-destructive">
                    LP can only be changed when the group balance is under $1.00 (current:{' '}
                    {group.usdValueOfAvailableBalance})
                  </p>
                )}
                {groupBalanceUsd > 0 && groupBalanceUsd < 1 && (
                  <p className="text-sm text-muted-foreground">
                    Saving an LP change will write off dust under $1.00 (current:{' '}
                    {group.usdValueOfAvailableBalance}).
                  </p>
                )}

                {selectedLp && (
                  <div className="text-sm text-muted-foreground">
                    {selectedLp.valiantBps != null ? (
                      <>
                        LP + Valiant: <span className="font-bold">{selectedLp.bps} BPS</span>
                      </>
                    ) : (
                      <>
                        LP Fee: <span className="font-bold">{selectedLp.bps} BPS</span>
                      </>
                    )}
                  </div>
                )}
                {selectedLp && (
                  <BPSCalculation
                    bps={parseInt(bps) || 0}
                    lpFee={selectedLp.bps}
                    valiantBps={selectedLp.valiantBps}
                  />
                )}
              </div>

              <div className="flex items-center justify-end gap-4 pt-4">
                <Button
                  variant="main_action"
                  type="submit"
                  disabled={isSubmitting || !hasChanges}
                  className="rounded-md"
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  type="button"
                  variant="main_outline"
                  className="rounded-md"
                  onClick={() => navigate(`/groups/${groupId}`)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Group Logo Upload */}
        <Card className="justify-start">
          <CardHeader>
            <CardTitle>Group Logo</CardTitle>
          </CardHeader>
          <CardContent>
            <FileUpload
              onFileUploaded={handleImageUploaded}
              onError={(error) => toast.error('Upload failed', { description: error })}
              currentImageUrl={imageUrl}
              entityType="group"
              entityId={groupId!}
              maxSizeInMB={1}
              onUploadRequest={handleUploadRequest}
            />
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  )
}
